import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { resolveGrantWithScope } from "../rbac/grantResources";
import { isTenantScopeGrant, SHAREABLE_SCOPE_KINDS } from "../rbac/permissions";
import type { BuiltinRoleId } from "../rbac/roles";
import { checkServiceAccountGrant } from "../rbac/serviceAccountGrantAuthorization";
import { validateServiceAccountGrants } from "../rbac/validation";
import { generateId } from "./utils";
import { mutator } from "./mutator";

// One grant the service account holds: a role on a target scope. Unlike
// setScopeRoleGrants (fixed scope, varying principal), here the principal is
// fixed (the service account) and each entry names a different scope.
const accountGrantEntry = z.object({
  scopeId: z.string(),
  scopeKind: z.enum(SHAREABLE_SCOPE_KINDS),
  builtinRoleId: z.string().optional(),
  customRoleId: z.string().optional(),
});

export const setServiceAccountGrantsSchema = z.object({
  serviceAccountId: z.string(),
  grants: z.array(accountGrantEntry),
});

function entryKey(g: {
  scopeId: string;
  builtinRoleId?: string | null;
  customRoleId?: string | null;
}): string {
  return `${g.scopeId}:${g.builtinRoleId ?? ""}:${g.customRoleId ?? ""}`;
}

// Sets the grants a service account holds (what it is allowed to do). Gated on
// service_account:manage for the account, PLUS a no-escalation guard: the
// granting user may only confer a permission they already hold on that scope.
// Without this, anyone who can manage an account could grant it -- and thus
// assume -- access they lack themselves. `hasAccess` lets a tenant admin
// (allow_all) through automatically.
export const setServiceAccountGrants = mutator({
  schema: setServiceAccountGrantsSchema,
  resolve: {
    serviceAccount: { kind: "serviceAccount", from: "serviceAccountId" },
    grantCustomRoles: {
      kind: "customRole",
      from: "grants",
      field: "customRoleId",
    },
  },
  permissions: [
    {
      permission: "service_account:manage",
      scopeId: ({ args }) => args.serviceAccount.id,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: { serviceAccount, grants, grantCustomRoles },
  }) => {
    const grantsWithScopes = await Promise.all(
      grants.map((grant) => resolveGrantWithScope(tx, grant, ctx.tenantId))
    );
    const resolvedGrants = validateServiceAccountGrants(
      grantsWithScopes,
      grantCustomRoles
    );

    for (const { input, role } of resolvedGrants) {
      const denial = await checkServiceAccountGrant({
        tx,
        ctx,
        scopeId: input.scopeId,
        role,
      });
      if (denial?.reason === "allow_all") {
        throw userFacingError(
          "Cannot grant a service account a role carrying allow_all"
        );
      }
      if (denial?.reason === "missing_permission") {
        throw userFacingError(
          `You cannot grant "${denial.permission}" because you do not hold it on this scope`
        );
      }
    }

    // Replace the account's resource-scoped grant set. Fetch existing grants
    // held by the account (as principal), diff by (scope, role), delete
    // removed, insert new. Tenant roles are excluded: they're managed
    // separately by setServiceAccountTenantRoles and would otherwise be wiped
    // out here since `grants` never carries a tenant-scoped entry.
    const existing = (
      await tx.run(
        zql.roleGrants
          .where("serviceAccountId", serviceAccount.id)
          .where("tenantId", ctx.tenantId)
      )
    ).filter((g) => !isTenantScopeGrant(g));
    const desiredKeys = new Set(grants.map(entryKey));
    const existingKeys = new Set(existing.map(entryKey));

    for (const row of existing) {
      if (!desiredKeys.has(entryKey(row))) {
        await tx.mutate.roleGrants.delete({ id: row.id });
      }
    }

    const now = Date.now();
    for (const grant of grants) {
      if (existingKeys.has(entryKey(grant))) continue;
      await tx.mutate.roleGrants.insert({
        id: generateId(),
        serviceAccountId: serviceAccount.id,
        builtinRoleId: (grant.builtinRoleId as BuiltinRoleId) ?? undefined,
        customRoleId: grant.customRoleId,
        scopeId: grant.scopeId,
        createdAt: now,
        tenantId: ctx.tenantId,
      });
    }
  },
});
